import { useBuildingBlueprints, type BuildingProjectionRow } from '@/api/hooks'
import { ResourceIcon, GoldIcon, WoodIcon, BuildingIcon, AnchorIcon, type ResourceName } from '@/components/game/GameIcons'
import { formatDuration } from '@/lib/time'
import { isInProjectionWindow } from '@/lib/projectionWindow'

interface Props {
  /** Building type key (e.g. "gold_mine"). */
  type: string
  /** The player's current level for this building — highlighted in the table. */
  currentLevel: number
}

/**
 * Level-by-level forecast for one building type, so the player can plan ahead:
 * production (with the per-level gain), cost and time for every reachable level.
 * Columns adapt to the building's role (producer / warehouse / shipyard / HQ).
 */
export function BuildingProjectionTable({ type, currentLevel }: Props) {
  const { data, isLoading, isError } = useBuildingBlueprints()
  const blueprint = data?.buildings.find((b) => b.type === type)

  if (isLoading) {
    return <p className="text-xs text-ocean-300 py-2">Chargement de l'évolution…</p>
  }
  if (isError || !blueprint) {
    return <p className="text-xs text-rum-500 py-2">Évolution indisponible.</p>
  }

  const isProducer = blueprint.projection.some((r) => Object.keys(r.productionPerHour).length > 0)
  const isWarehouse = blueprint.projection.some((r) => r.storageCapacity > 0)
  const isShipyard = type === 'shipyard'
  const isCapitainerie = type === 'capitainerie'
  const isHq = type === 'hq'

  const valueHeader = isProducer
    ? 'Production'
    : isWarehouse
      ? 'Capacité'
      : isShipyard
        ? 'Chantiers'
        : isCapitainerie
          ? 'Mouillages'
          : isHq
            ? 'Créneaux'
            : '—'

  return (
    <div className="mt-2 max-h-64 overflow-auto rounded border border-ocean-500/20 bg-ocean-900/40">
      <table className="w-full min-w-[320px] text-xs text-ocean-100">
        <thead className="sticky top-0 bg-ocean-900 text-ocean-300">
          <tr>
            <th className="px-2 py-1.5 text-left font-semibold">Niv.</th>
            <th className="px-2 py-1.5 text-left font-semibold">{valueHeader}</th>
            <th className="px-2 py-1.5 text-right font-semibold">Coût</th>
            <th className="px-2 py-1.5 text-right font-semibold">Temps</th>
          </tr>
        </thead>
        <tbody>
          {blueprint.projection.map((row, idx) => {
            // Fenêtre glissante : ±5 paliers autour du niveau courant (les bâtiments
            // ne sont pas plafonnés → une table 1..N complète serait du bruit).
            if (!isInProjectionWindow(row.level, currentLevel)) return null
            const prev = idx > 0 ? blueprint.projection[idx - 1] : null
            const isCurrent = row.level === currentLevel
            const isNext = row.level === currentLevel + 1
            return (
              <tr
                key={row.level}
                className={
                  isCurrent
                    ? 'bg-sand-500/20 text-sand-100'
                    : isNext
                      ? 'bg-ocean-500/10'
                      : 'odd:bg-ocean-700/20'
                }
              >
                <td className="px-2 py-1 whitespace-nowrap">
                  {row.level}
                  {isCurrent && <span className="ml-1 text-[10px] text-sand-300">actuel</span>}
                  {isNext && <span className="ml-1 text-[10px] text-ocean-300">suivant</span>}
                </td>
                <td className="px-2 py-1">
                  <ValueCell row={row} prev={prev} isProducer={isProducer} isWarehouse={isWarehouse} isShipyard={isShipyard} isCapitainerie={isCapitainerie} isHq={isHq} />
                </td>
                <td className="px-2 py-1 text-right whitespace-nowrap">
                  {row.level === 1 ? (
                    <span className="text-ocean-400">départ</span>
                  ) : (
                    <span className="inline-flex items-center justify-end gap-1">
                      <GoldIcon className="w-3.5 h-3.5 text-sand-300" />{row.upgradeCost.gold.toLocaleString('fr-FR')}
                      <WoodIcon className="w-3.5 h-3.5 text-sand-300 ml-1.5" />{row.upgradeCost.wood.toLocaleString('fr-FR')}
                    </span>
                  )}
                </td>
                <td className="px-2 py-1 text-right whitespace-nowrap text-ocean-300">
                  {row.level === 1 ? '—' : formatDuration(row.upgradeTimeSeconds)}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function ValueCell({
  row,
  prev,
  isProducer,
  isWarehouse,
  isShipyard,
  isCapitainerie,
  isHq,
}: {
  row: BuildingProjectionRow
  prev: BuildingProjectionRow | null
  isProducer: boolean
  isWarehouse: boolean
  isShipyard: boolean
  isCapitainerie: boolean
  isHq: boolean
}) {
  if (isProducer) {
    const [res, rate] = Object.entries(row.productionPerHour)[0] ?? ['gold', 0]
    const prevRate = prev?.productionPerHour[res] ?? 0
    const gain = rate - prevRate
    return (
      <span className="inline-flex items-center gap-1 whitespace-nowrap">
        <ResourceIcon name={res as ResourceName} className="w-3.5 h-3.5 text-sand-300" /> {rate.toLocaleString('fr-FR')}/h
        {gain > 0 && <span className="ml-1 text-[10px] text-green-300">+{gain}</span>}
      </span>
    )
  }
  if (isWarehouse) {
    const gain = row.storageCapacity - (prev?.storageCapacity ?? 0)
    return (
      <span className="inline-flex items-center gap-1 whitespace-nowrap">
        <BuildingIcon type="warehouse" className="w-3.5 h-3.5 text-sand-300" /> {row.storageCapacity.toLocaleString('fr-FR')}
        {gain > 0 && <span className="ml-1 text-[10px] text-green-300">+{gain.toLocaleString('fr-FR')}</span>}
      </span>
    )
  }
  if (isShipyard) {
    return (
      <span className="inline-flex items-center gap-1 whitespace-nowrap">
        <AnchorIcon className="w-3.5 h-3.5 text-sand-300" /> {row.shipyardSlots} chantier{row.shipyardSlots > 1 ? 's' : ''}
        <span className="ml-1 text-[10px] text-ocean-300">
          carène {(row.careenRatePerHour * 100).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %/h
        </span>
      </span>
    )
  }
  if (isCapitainerie) {
    const gain = row.capitainerieBerths - (prev?.capitainerieBerths ?? 0)
    return (
      <span className="inline-flex items-center gap-1 whitespace-nowrap">
        <BuildingIcon type="capitainerie" className="w-3.5 h-3.5 text-sand-300" /> {row.capitainerieBerths} mouillages
        {gain > 0 && <span className="ml-1 text-[10px] text-green-300">+{gain}</span>}
      </span>
    )
  }
  if (isHq) {
    // Le palier où un créneau s'ouvre est le seul événement notable de cette
    // colonne : on le marque, sinon la table n'est qu'une colonne de « 1 ».
    const gain = row.upgradeSlots - (prev?.upgradeSlots ?? row.upgradeSlots)
    return (
      <span className="inline-flex items-center gap-1 whitespace-nowrap">
        <BuildingIcon type="hq" className="w-3.5 h-3.5 text-sand-300" /> {row.upgradeSlots} créneau
        {row.upgradeSlots > 1 ? 'x' : ''}
        {gain > 0 && <span className="ml-1 text-[10px] font-semibold text-green-300">+{gain}</span>}
      </span>
    )
  }
  return <span className="text-ocean-400">débloque les niveaux</span>
}
